import {
  deleteTaskAppendix,
  queryTasksV2,
  uploadTaskAppendix,
} from '@/services/api/task.api';
import { getRecordDocument } from '@/services/api/record.api';
import { importTasksV2, queryTablesV2 } from '@/services/api/v2/connect_v2';

const state = {
  productionTasks: [],
  focusedTask: null,
  focusedTaskElements: [],
  moduleTableId: null,
  focusedTaskModules: [],
  focusedTaskAssemblyTasks: [],
  focusedTaskElementTypes: [],
  focusedLane: null,
  productionHalls: [],
  productionConfigurations: [],
  productionLocations: [],
  calendar: [],
  requestCounter: 0,
  projectElementAssemblyTasks: [],
  importCounter: 0,
};
const mutations = {
  make_request(state) {
    state.requestCounter++;
  },
  request_success(state) {
    state.requestCounter--;
  },
  production_tasks_success(state, packages) {
    state.productionTasks = packages;
    state.requestCounter--;
  },
  update_production_tasks(state, tasks) {
    const list = Array.isArray(tasks) ? tasks : [];

    for (const t1 of list) {
      const idx = state.productionTasks.findIndex((t2) => t2.id === t1.id);

      if (idx === -1) {
        state.productionTasks.push(t1);
      } else {
        state.productionTasks[idx] = {
          ...state.productionTasks[idx],
          ...t1,
        };
      }
    }
  },
  focus_on_task(state, task) {
    state.focusedTask = task;
  },
  focus_on_lane(state, lane) {
    state.focusedLane = lane;
  },
  focused_task_data_success(
    state,
    { tasks, types, moduleTableId, modules, elements }
  ) {
    state.focusedTaskAssemblyTasks = tasks;
    state.focusedTaskElementTypes = types;
    state.moduleTableId = moduleTableId;
    state.focusedTaskModules = modules;
    state.focusedTaskElements = elements;
    state.requestCounter--;
  },
  production_halls_success(
    state,
    { calendar, halls, configurations, locations }
  ) {
    state.calendar = calendar;
    state.productionHalls = halls;
    state.productionConfigurations = configurations;
    state.productionLocations = locations;
    state.requestCounter--;
  },
  project_element_assembly_tasks_success(state, { tasks }) {
    state.projectElementAssemblyTasks = tasks;
  },
};
const actions = {
  async fetchProductionTasks({ commit, rootGetters }) {
    try {
      commit('make_request');
      const response = await queryTasksV2(rootGetters.project.license, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-work-package'],
        },
      ]);
      commit('production_tasks_success', response.tasks);
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    }
  },
  async fetchProductionHallsAndConfigurations({ commit, rootGetters }) {
    try {
      commit('make_request');
      const { productionHalls, calendar } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_HALLS',
            project: rootGetters.project.id,
            as: 'productionHalls',
            columns: [
              {
                name: 'hall',
                as: 'title',
              },
              {
                name: 'type',
                conditions: [
                  {
                    operator: '=',
                    value: 'production',
                  },
                ],
              },
            ],
          },
          {
            name: 'CFFA_DHME_CALENDAR',
            project: rootGetters.project.id,
            as: 'calendar',
            columns: [
              {
                name: 'label',
              },
              {
                name: 'date',
              },
            ],
          },
        ],
      });
      const { configurations, locations } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_ASSEMBLY_LOCATIONS',
            project: rootGetters.project.id,
            as: 'locations',
            columns: [
              {
                name: 'assembly_hall',
                conditions: productionHalls.records.map((r) => {
                  return {
                    operator: '=',
                    value: r.id,
                    boolean: 'or',
                  };
                }),
              },
              {
                name: 'assembly_lane',
              },
              {
                name: 'assembly_location',
              },
              {
                name: 'description',
              },
              {
                name: 'hall_configuration',
                as: 'configuration',
              },
            ],
          },
          {
            name: 'CFFA_DHME_ASSEMBLY_HALL_CONFIGURATIONS',
            project: rootGetters.project.id,
            as: 'configurations',
            columns: [
              {
                name: 'assembly_hall',
                conditions: productionHalls.records.map((r) => {
                  return {
                    operator: '=',
                    value: r.id,
                    boolean: 'or',
                  };
                }),
              },
              {
                name: 'title',
              },
            ],
          },
        ],
      });

      commit('production_halls_success', {
        calendar: calendar.records,
        halls: productionHalls.records,
        configurations: configurations.records,
        locations: locations.records,
      });
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    }
  },
  async fetchElementsInGroupInOrder({ commit, rootGetters, getters }) {
    try {
      commit('make_request');
      const tasksResponse = await queryTasksV2(rootGetters.project.license, [
        {
          column: 'project',
          operator: '=',
          values: [getters.focusedTask?.task_project.id],
        },
        {
          column: 'type',
          operator: '=',
          values: ['dhme-module-assembly'],
        },
      ]);
      const { typesInGroup } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_RESOURCEGROUP_ELEMENTS',
            project: getters.focusedTask?.task_project.id,
            as: 'typesInGroup',
            sortBy: 'element_type',
            columns: [
              {
                name: 'group_code',
                conditions: [
                  {
                    operator: '=',
                    value: getters.focusedTask.task_type.custom_2,
                  },
                ],
              },
              {
                name: 'element_type',
              },
            ],
          },
        ],
      });
      const { modules, elements } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_MODULES',
            project: getters.focusedTask?.task_project.id,
            as: 'modules',
            columns: [
              {
                name: 'module_id',
              },
              {
                name: '2d_drawing',
              },
            ],
          },
          {
            name: 'CFFA_DHME_ELEMENTS',
            project: getters.focusedTask?.task_project.id,
            as: 'elements',
            columns: [
              {
                name: 'module_id',
                conditions: tasksResponse.tasks.map((t) => {
                  return {
                    operator: '=',
                    value: t.sbscode.code,
                    boolean: 'or',
                  };
                }),
              },
              {
                name: 'element_id',
              },
              {
                name: 'element_type',
                conditions: typesInGroup.records.map((r) => {
                  return {
                    operator: '=',
                    value: r.element_type,
                    boolean: 'or',
                  };
                }),
              },
            ],
          },
        ],
      });

      commit('focused_task_data_success', {
        tasks: tasksResponse.tasks,
        types: typesInGroup.records,
        modules: modules.records,
        moduleTableId: modules.id,
        elements: elements.records,
      });

      const { tasks } = await queryTasksV2(rootGetters.project.license, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-element-assembly'],
        },
        {
          column: 'project',
          operator: '=',
          values: [getters.focusedTask?.task_project?.id],
        },
      ]);
      commit('project_element_assembly_tasks_success', {
        tasks: tasks,
      });
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    }
  },

  async importProductionTasks({ commit, rootGetters, getters, state }, tasks) {
    try {
      commit('make_request');

      const projectId = getters.focusedTask?.task_project?.id;
      const licenseId = rootGetters.project?.license;

      if (!projectId || !licenseId) {
        commit('request_success');
        return;
      }

      function chunkArray(array, size) {
        const chunks = [];
        for (let i = 0; i < array.length; i += size)
          chunks.push(array.slice(i, i + size));
        return chunks;
      }

      // 1) Import in chunks (do not rely on return value)
      const taskChunks = chunkArray(tasks || [], 50);
      for (const chunk of taskChunks) {
        await importTasksV2(chunk);
      }

      // 2) Refetch all element-assembly tasks for this project
      const { tasks: fetchedElementTasks } = await queryTasksV2(licenseId, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-element-assembly'],
        },
        {
          column: 'project',
          operator: '=',
          values: [projectId],
        },
      ]);

      // 3) Lookup by sbscode.code => task
      const taskByElementId = new Map();
      for (const t of fetchedElementTasks || []) {
        const code = t?.sbscode?.code;
        if (code) taskByElementId.set(code, t);
      }

      // 4) Upload drawings per element-task (using module 2d drawing)
      state.importCounter = 0;

      const modulesWithDrawing = (getters.focusedTaskModules || []).filter(
        (m) => m?.['2d_drawing']
      );

      for (const m of modulesWithDrawing) {
        const elements = (getters.focusedTaskElements || []).filter(
          (e) => e.module_id === m.module_id
        );

        // Important: your previous call used m['2d_drawing'] vs m['2d_drawing']?.id inconsistently.
        // Keep it consistent with getRecordDocument expectations (same as your other store):
        const recordId = m['2d_drawing']?.id ?? m['2d_drawing'];

        // Fetch the drawing once per module (reuse for all its elements)
        const document = recordId
          ? await getRecordDocument(recordId, projectId, getters.moduleTableId)
          : null;

        for (const e of elements) {
          const task = taskByElementId.get(e.element_id);
          if (!task) continue;

          const appendixName = `2D_${m.module_id}`;
          const existing = (task.appendixes || []).find(
            (a) => a.name === appendixName
          );

          if (existing) {
            await deleteTaskAppendix(task.id, existing.id);
          }

          if (document?.file) {
            await uploadTaskAppendix(task.id, {
              data: document.file,
              extension: document.extension,
              name: appendixName,
            });
          }
        }

        state.importCounter++;
      }

      commit('request_success');

      // This store tracks work-packages in productionTasks; keep that consistent:
      // refetch work packages, or (if you already have them) merge safely.
      // Quick + consistent approach: refetch work packages too.
      const { tasks: fetchedWorkPackages } = await queryTasksV2(licenseId, [
        {
          column: 'type',
          operator: '=',
          values: ['dhme-work-package'],
        },
        {
          column: 'project',
          operator: '=',
          values: [projectId],
        },
      ]);

      commit('production_tasks_success', fetchedWorkPackages || []);
    } catch (e) {
      this.commit('showNotification', {
        content: e.message,
        color: 'error',
      });
    }
  },
};
const getters = {
  requestedProductionTasks: (state) =>
    state.productionTasks.filter((t) => t.task_type.custom_1 === 'request'),
  plannedProductionTasks: (state) =>
    state.productionTasks.filter((t) => t.task_type.custom_1 === 'planned'),
  focusedTask: (state) => state.focusedTask,
  focusedLane: (state) => state.focusedLane,
  focusedTaskAssemblyTasks: (state) => state.focusedTaskAssemblyTasks,
  focusedTaskElements: (state) => state.focusedTaskElements,
  moduleTableId: (state) => state.moduleTableId,
  focusedTaskModules: (state) => state.focusedTaskModules,
  focusedTaskElementTypes: (state) => state.focusedTaskElementTypes,
  productionHalls: (state) => state.productionHalls,
  productionConfigurations: (state) => state.productionConfigurations,
  productionLocations: (state) => state.productionLocations,
  vacationCalendar: (state) => state.calendar,
  isLoading: (state) => state.requestCounter > 0,
  projectElementAssemblyTasks: (state) => state.projectElementAssemblyTasks,
  importCounter: (state) => state.importCounter,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
